import { ToolLayout } from '../../components/tools/ToolLayout';

interface ExchangeInfo {
  name: string;
  country: string;
  takerFee: string;
  makerFee: string;
  brlDeposit: string;
  brlWithdraw: string;
  kyc: string;
  features: string[];
  support: string;
}

// Dados editoriais verificados em julho/2026 nas páginas oficiais de taxas.
// Sem ranking pago: ordem alfabética.
const EXCHANGES: ExchangeInfo[] = [
  {
    name: 'Binance',
    country: 'Global (atende o Brasil)',
    takerFee: '0,10%',
    makerFee: '0,10%',
    brlDeposit: 'Pix (grátis)',
    brlWithdraw: 'Pix',
    kyc: 'Obrigatório',
    features: ['Maior liquidez global', 'Staking', 'Futuros', 'App em português'],
    support: 'Chat 24h em português',
  },
  {
    name: 'Bitget',
    country: 'Global (atende o Brasil)',
    takerFee: '0,10%',
    makerFee: '0,10%',
    brlDeposit: 'Pix',
    brlWithdraw: 'Pix',
    kyc: 'Obrigatório',
    features: ['Copy trading', 'Futuros', 'Staking'],
    support: 'Chat 24h',
  },
  {
    name: 'Coinbase',
    country: 'Estados Unidos',
    takerFee: '≈ 0,60%',
    makerFee: '≈ 0,40%',
    brlDeposit: 'Pix (via parceiros)',
    brlWithdraw: 'Pix (via parceiros)',
    kyc: 'Obrigatório',
    features: ['Empresa listada em bolsa (NASDAQ)', 'Custódia regulada', 'Interface simples'],
    support: 'Central de ajuda em português',
  },
  {
    name: 'Foxbit',
    country: 'Brasil',
    takerFee: '0,50%',
    makerFee: '0,25%',
    brlDeposit: 'Pix (grátis)',
    brlWithdraw: 'Pix',
    kyc: 'Obrigatório (CPF)',
    features: ['Empresa brasileira', 'Suporte local', 'Integração bancária nacional'],
    support: 'Suporte em português (horário comercial)',
  },
  {
    name: 'Mercado Bitcoin',
    country: 'Brasil',
    takerFee: '0,60%',
    makerFee: '0,30%',
    brlDeposit: 'Pix (grátis)',
    brlWithdraw: 'Pix',
    kyc: 'Obrigatório (CPF)',
    features: ['Maior exchange brasileira', 'Regulada localmente', 'Renda fixa digital e staking'],
    support: 'Suporte em português',
  },
  {
    name: 'OKX',
    country: 'Global (atende o Brasil)',
    takerFee: '0,10%',
    makerFee: '0,08%',
    brlDeposit: 'Pix',
    brlWithdraw: 'Pix',
    kyc: 'Obrigatório',
    features: ['Carteira Web3 integrada', 'Futuros', 'Boa liquidez'],
    support: 'Chat 24h',
  },
];

export const ComparadorExchangesPage: React.FC = () => {
  return (
    <ToolLayout
      path="/ferramentas/comparador-exchanges"
      title="Comparador de Exchanges"
      metaTitle="Comparador de Exchanges de Criptomoedas no Brasil"
      metaDescription="Compare taxas, depósito em reais via Pix, KYC, recursos e atendimento das principais exchanges de criptomoedas disponíveis para brasileiros. Conteúdo editorial, sem ranking pago."
      intro={[
        'Escolher uma exchange envolve mais do que a taxa de negociação: importam a facilidade de depositar e sacar em reais, a exigência de verificação de identidade (KYC), os recursos oferecidos, a segurança e o atendimento.',
        'A tabela abaixo compara as principais exchanges disponíveis para brasileiros. As informações são editoriais, verificadas nas páginas oficiais de cada empresa, apresentadas em ordem alfabética — nenhuma posição é paga ou patrocinada.',
      ]}
      explanation={
        <section className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Como avaliar uma exchange</h2>
          <div className="text-gray-700 text-[15px] leading-relaxed space-y-3">
            <p>
              <strong>Taxas:</strong> as taxas de negociação (maker/taker) incidem em cada operação, mas
              observe também o spread e as taxas de saque — em exchanges com negociação "grátis", o custo
              costuma estar embutido no preço.
            </p>
            <p>
              <strong>Segurança:</strong> prefira empresas com histórico público, prova de reservas ou
              auditorias, autenticação em dois fatores e, para valores relevantes, considere custódia
              própria em carteira fria (veja nosso glossário).
            </p>
            <p>
              <strong>KYC:</strong> todas as exchanges listadas exigem verificação de identidade, o que é
              um sinal de conformidade regulatória — desconfie de plataformas que prometem anonimato total.
            </p>
            <p>
              <strong>Reais (BRL):</strong> depósito e saque via Pix são hoje o padrão do mercado
              brasileiro; confirme limites e prazos na página oficial antes de operar.
            </p>
          </div>
        </section>
      }
      methodology={[
        'Taxas e condições verificadas nas páginas oficiais de tarifas de cada exchange em julho de 2026.',
        'Valores referem-se ao tier básico de pessoa física, sem descontos por volume ou token nativo.',
        'Ordem alfabética — não há ranking, nota ou posicionamento pago.',
        'Taxas e serviços mudam com frequência: confirme sempre na página oficial antes de operar.',
      ]}
      faqs={[
        {
          question: 'Qual é a melhor exchange?',
          answer:
            'Depende do seu perfil. Para quem prioriza suporte local e Pix nativo, as brasileiras são convenientes; para quem busca liquidez e variedade de ativos, as globais costumam levar vantagem. Compare taxas totais (negociação + saque + spread) para o seu volume.',
        },
        {
          question: 'O Cripto News recebe comissão dessas exchanges?',
          answer:
            'Não. A tabela é conteúdo editorial independente, em ordem alfabética, sem links de afiliados e sem posições pagas. Se isso mudar um dia, haverá identificação explícita, conforme nossa Política Editorial.',
        },
        {
          question: 'Deixar cripto na exchange é seguro?',
          answer:
            'Exchanges reputadas investem em segurança, mas manter grandes valores sob custódia de terceiros sempre envolve risco (hacks, insolvência). Para reservas de longo prazo, muitos usuários preferem carteiras próprias — entenda os prós e contras no nosso glossário.',
        },
      ]}
    >
      <div className="bg-white rounded-xl shadow mb-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-3">Exchange</th>
              <th className="px-4 py-3">Origem</th>
              <th className="px-4 py-3">Taxa taker</th>
              <th className="px-4 py-3">Taxa maker</th>
              <th className="px-4 py-3">Depósito R$</th>
              <th className="px-4 py-3">Saque R$</th>
              <th className="px-4 py-3">KYC</th>
              <th className="px-4 py-3">Recursos</th>
              <th className="px-4 py-3">Atendimento</th>
            </tr>
          </thead>
          <tbody>
            {EXCHANGES.map((exchange) => (
              <tr key={exchange.name} className="border-t border-gray-100 align-top">
                <td className="px-4 py-3 font-bold text-gray-900">{exchange.name}</td>
                <td className="px-4 py-3 text-gray-600">{exchange.country}</td>
                <td className="px-4 py-3 text-gray-800">{exchange.takerFee}</td>
                <td className="px-4 py-3 text-gray-800">{exchange.makerFee}</td>
                <td className="px-4 py-3 text-gray-600">{exchange.brlDeposit}</td>
                <td className="px-4 py-3 text-gray-600">{exchange.brlWithdraw}</td>
                <td className="px-4 py-3 text-gray-600">{exchange.kyc}</td>
                <td className="px-4 py-3 text-gray-600">
                  <ul className="list-disc pl-4 space-y-0.5">
                    {exchange.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </td>
                <td className="px-4 py-3 text-gray-600">{exchange.support}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 px-4 py-3 border-t border-gray-100">
          Dados editoriais verificados em julho/2026 nas páginas oficiais. Taxas do tier básico, sujeitas a
          alteração — confirme na exchange antes de operar. Ordem alfabética; sem ranking pago.
        </p>
      </div>
    </ToolLayout>
  );
};
